import React, { useEffect, useState } from 'react';
import { Close } from "@mui/icons-material";
import api from '../../services/api';

interface ProjetoExportarModalProps {
  projeto: any;
  onClose: () => void;
}

interface PreviaExportacao {
  contagens: { registros: number; itens: number; petrechos: number; locais: number; especies: number; pescadores: number };
  avisos: string[];
  formatoRecomendado: 'xlsx' | 'csv';
  xlsxPossivel: boolean;
}

const doisDigitos = (n: number) => String(n).padStart(2, '0');
// Datas no calendário LOCAL do navegador (toISOString() devolveria o dia seguinte depois das 21h em Brasília).
const dataLocal = (d: Date) => `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}`;
const hoje = dataLocal(new Date());
const primeiroDiaDoMes = `${hoje.slice(0, 7)}-01`;

// Só consulta/baixa com as duas datas completas e válidas (aaaa-mm-dd, ano de 4 dígitos entre 1900 e 2100, início <= fim).
const periodoValido = (ini: string, fim: string): string | null => {
  const ok = (d: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (!m) return false;
    const [a, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (a < 1900 || a > 2100) return false;
    const dt = new Date(Date.UTC(a, mes - 1, dia));
    return dt.getUTCFullYear() === a && dt.getUTCMonth() === mes - 1 && dt.getUTCDate() === dia;
  };
  if (!ini || !fim) return 'Informe data inicial e data final.';
  if (!ok(ini) || !ok(fim)) return 'Informe datas válidas (ano com 4 dígitos).';
  if (ini > fim) return 'A data inicial não pode ser depois da data final.';
  return null;
};

const MOTIVO_MIN = 10;

const mensagemDoErro = async (err: any, padrao: string): Promise<string> => {
  if (err?.response?.data instanceof Blob) {
    try {
      return JSON.parse(await err.response.data.text())?.message || padrao;
    } catch {
      return padrao; // corpo do erro não veio em JSON
    }
  }
  return err?.response?.data?.message || padrao;
};

// Exportação de dados brutos do projeto (análise externa): várias abas relacionáveis por ID, em .xlsx ou .zip de CSVs.
// Padrão PSEUDONIMIZADO (pescador só por código anônimo, por projeto), só produção. Observações, dados pessoais e
// registros de teste/demonstração: só administradores, com motivo — e toda exportação fica registrada em log.
const ProjetoExportarModal: React.FC<ProjetoExportarModalProps> = ({ projeto, onClose }) => {
  const isAdmin = localStorage.getItem('modulo') === 'admin';
  const [dataInicial, setDataInicial] = useState(primeiroDiaDoMes);
  const [dataFinal, setDataFinal] = useState(hoje);
  const [formato, setFormato] = useState<'xlsx' | 'csv'>('xlsx');
  const [validacao, setValidacao] = useState<'todos' | 'somente_validados'>('todos');
  const [incluirObservacoes, setIncluirObservacoes] = useState(false);
  const [incluirPessoais, setIncluirPessoais] = useState(false);
  const [incluirTeste, setIncluirTeste] = useState(false);
  const [incluirDemo, setIncluirDemo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [previa, setPrevia] = useState<PreviaExportacao | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState('');

  const algumaLiberacao = incluirObservacoes || incluirPessoais || incluirTeste || incluirDemo;
  const erroPeriodo = periodoValido(dataInicial, dataFinal);

  const montarParametros = () => {
    const params: Record<string, string> = { data_inicial: dataInicial, data_final: dataFinal, formato, validacao };
    if (incluirObservacoes) params.incluir_observacoes = 'true';
    if (incluirPessoais) params.incluir_pessoais = 'true';
    if (incluirTeste) params.incluir_teste = 'true';
    if (incluirDemo) params.incluir_demo = 'true';
    if (algumaLiberacao) params.motivo = motivo.trim();
    return params;
  };

  // Prévia (contagens e avisos do recorte) 500 ms depois da última mudança, só com período válido.
  useEffect(() => {
    if (erroPeriodo) { setPrevia(null); return; }
    let ativo = true;
    const t = setTimeout(() => {
      setCarregandoPrevia(true);
      const params: Record<string, string> = { data_inicial: dataInicial, data_final: dataFinal, validacao };
      if (incluirTeste) params.incluir_teste = 'true';
      if (incluirDemo) params.incluir_demo = 'true';
      // a prévia não precisa de motivo: manda as liberações só quando o motivo já está preenchido
      if (incluirTeste || incluirDemo) params.motivo = motivo.trim() || 'previa sem motivo informado ainda';
      api.get(`/pesca/projetos/${projeto.uid_pesca_projeto}/exportar/previa`, { params })
        .then((res) => {
          if (!ativo) return;
          setPrevia(res.data.data);
          if (!res.data.data.xlsxPossivel) setFormato('csv');
          else if (res.data.data.formatoRecomendado === 'csv') setFormato((f) => (f === 'xlsx' ? 'csv' : f));
          setErro('');
        })
        .catch(async (e) => { if (ativo) { setPrevia(null); setErro(await mensagemDoErro(e, 'Não foi possível calcular a prévia da exportação.')); } })
        .finally(() => { if (ativo) setCarregandoPrevia(false); });
    }, 500);
    return () => { ativo = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicial, dataFinal, validacao, incluirTeste, incluirDemo, projeto.uid_pesca_projeto]);

  const handleGerar = async () => {
    if (erroPeriodo) { setErro(erroPeriodo); return; }
    if (algumaLiberacao && motivo.trim().length < MOTIVO_MIN) { setErro(`Informe o motivo (mínimo ${MOTIVO_MIN} caracteres) para liberar dados além da versão pseudonimizada.`); return; }
    setGerando(true);
    setErro('');
    try {
      const res = await api.get(`/pesca/projetos/${projeto.uid_pesca_projeto}/exportar`, { params: montarParametros(), responseType: 'blob' });
      const extensao = formato === 'csv' ? 'zip' : 'xlsx';
      const blob = new Blob([res.data], { type: formato === 'csv' ? 'application/zip' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `exportacao-${projeto.nome.replace(/[^\w-]+/g, '_')}-${dataInicial}_a_${dataFinal}.${extensao}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      onClose();
    } catch (err: any) {
      setErro(await mensagemDoErro(err, 'Não foi possível gerar a exportação.'));
    } finally {
      setGerando(false);
    }
  };

  const n = (v: number) => v.toLocaleString('pt-BR');

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)', fontSize: '1.2rem' }}>Exportar dados — {projeto.nome}</h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: '#666', lineHeight: 1.45 }}>
          Gera as abas <strong>registros</strong> (1 linha por pescaria), <strong>itens_captura</strong> (1 por espécie, com peso informado, fator e peso convertido),
          petrechos, locais (hierarquia e coordenadas), espécies, pescadores (só por código anônimo), dicionário de dados e metadados. As abas se relacionam por ID.
          Só dados de produção, sem registros apagados.
        </p>

        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ ...fieldWrapStyle, flex: 1 }}>
            <label style={labelStyle}>Data inicial</label>
            <input type="date" style={inputStyle} value={dataInicial} min="1900-01-01" max="2100-12-31" onChange={(e) => setDataInicial(e.target.value)} />
          </div>
          <div style={{ ...fieldWrapStyle, flex: 1 }}>
            <label style={labelStyle}>Data final</label>
            <input type="date" style={inputStyle} value={dataFinal} min="1900-01-01" max="2100-12-31" onChange={(e) => setDataFinal(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ ...fieldWrapStyle, flex: 1 }}>
            <label style={labelStyle}>Formato</label>
            <select style={inputStyle} value={formato} onChange={(e) => setFormato(e.target.value as 'xlsx' | 'csv')}>
              <option value="xlsx" disabled={previa ? !previa.xlsxPossivel : false}>Planilha Excel (.xlsx)</option>
              <option value="csv">CSV (.zip, um arquivo por aba)</option>
            </select>
          </div>
          <div style={{ ...fieldWrapStyle, flex: 1 }}>
            <label style={labelStyle}>Validação dos registros</label>
            <select style={inputStyle} value={validacao} onChange={(e) => setValidacao(e.target.value as 'todos' | 'somente_validados')}>
              <option value="todos">Todos (com a coluna de status)</option>
              <option value="somente_validados">Somente validados</option>
            </select>
          </div>
        </div>

        {/* PRÉVIA DO RECORTE */}
        <div style={{ background: '#f6fafc', border: '1px solid #e1eef5', borderRadius: '8px', padding: '10px 12px', fontSize: '0.8rem', color: '#455', minHeight: '44px', opacity: carregandoPrevia ? 0.6 : 1 }}>
          {erroPeriodo ? <span style={{ color: '#a15c00' }}>{erroPeriodo}</span> : previa ? (
            <>
              <strong>{n(previa.contagens.registros)}</strong> pescarias · <strong>{n(previa.contagens.itens)}</strong> itens de captura · {n(previa.contagens.petrechos)} petrechos · {n(previa.contagens.locais)} locais · {n(previa.contagens.especies)} espécies · {n(previa.contagens.pescadores)} pescadores
              {previa.avisos.length > 0 && (
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: '#7a5a00' }}>
                  {previa.avisos.map((a) => <li key={a}>{a}</li>)}
                </ul>
              )}
              {previa.formatoRecomendado === 'csv' && <div style={{ marginTop: 4 }}>Recorte grande: o CSV (.zip) é o formato recomendado.</div>}
            </>
          ) : (carregandoPrevia ? 'Calculando...' : 'Sem prévia.')}
        </div>

        {isAdmin && (
          <details style={{ marginTop: '12px' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#a15c00', fontWeight: 600 }}>Opções avançadas (somente administradores)</summary>
            <div style={{ marginTop: '8px', fontSize: '0.82rem', color: '#555' }}>
              <label style={checkStyle}><input type="checkbox" checked={incluirObservacoes} onChange={(e) => setIncluirObservacoes(e.target.checked)} /> Incluir observações (texto livre; pode conter nomes)</label>
              <label style={checkStyle}><input type="checkbox" checked={incluirPessoais} onChange={(e) => setIncluirPessoais(e.target.checked)} /> Incluir dados pessoais dos pescadores (aba separada)</label>
              <label style={checkStyle}><input type="checkbox" checked={incluirTeste} onChange={(e) => setIncluirTeste(e.target.checked)} /> Incluir registros de teste/demonstração</label>
              <label style={checkStyle}><input type="checkbox" checked={incluirDemo} onChange={(e) => setIncluirDemo(e.target.checked)} /> Incluir registros DEMO-GEO (locais sintéticos de demonstração)</label>
              {algumaLiberacao && (
                <div style={{ marginTop: '8px' }}>
                  <label style={labelStyle}>Motivo da exportação (obrigatório, fica registrado em log com seu usuário)</label>
                  <textarea style={{ ...inputStyle, minHeight: '60px' }} value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: pedido formal do Instituto de Pesca, ofício nº ..." />
                </div>
              )}
            </div>
          </details>
        )}

        {erro && <p role="alert" style={{ color: '#f75a68', fontSize: '0.85rem', marginBottom: 0 }}>{erro}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
          <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
          <button type="button" onClick={handleGerar} disabled={gerando || !!erroPeriodo} style={{ ...primaryBtnStyle, opacity: gerando || erroPeriodo ? 0.6 : 1 }}>
            {gerando ? 'Gerando...' : 'Gerar/Baixar'}
          </button>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: '0.72rem', color: '#999' }}>Cada exportação fica registrada (quem, quando, filtros).</p>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '92%', maxWidth: '620px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '12px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const checkStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', cursor: 'pointer' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};

export default ProjetoExportarModal;
